<template>
  <nav class="ib-sidebar">
    <!-- Top section: header + navigation -->
    <div class="sidebar-top">
      <!-- Community header -->
      <div class="community-header">
        <!-- Avatar + Name -->
        <div class="community-identity">
          <div class="community-avatar">
            <img
              v-if="communityAvatar"
              :src="communityAvatar"
              class="avatar-img"
            />
            <div v-else class="avatar-fallback">{{ getInitials(communityName) }}</div>
          </div>
          <div class="community-name-wrap">
            <div class="community-name">{{ communityName }}</div>
          </div>
        </div>

        <!-- Members chip -->
        <div class="members-chip">
          <v-icon size="16" color="#C03131">fa-regular fa-users</v-icon>
          <div class="members-label">Members:</div>
          <div class="members-count">{{ memberCount }}</div>
        </div>

        <!-- Sponsored By -->
        <div class="sponsored-row" v-if="sponsorLogo || sponsorName">
          <div class="sponsored-text">Sponsored By</div>
          <img
            v-if="sponsorLogo"
            :src="sponsorLogo"
            class="partner-logo-small"
          />
          <div v-else class="sponsored-text sponsor-name">{{ sponsorName }}</div>
        </div>
      </div>

      <!-- Navigation -->
      <div class="nav-section">
        <!-- Community divider -->
        <div class="section-divider">
          <div class="section-label">Community</div>
          <div class="section-line"></div>
        </div>

        <!-- Community nav items -->
        <router-link
          v-for="item in communityNav"
          :key="item.name"
          :to="item.to"
          custom
          v-slot="{ navigate, isActive }"
        >
          <div
            class="nav-item"
            :class="{ active: isActive }"
            @click="navigate"
          >
            <v-icon size="16" :color="isActive ? '#F8F8F8' : '#333'">{{ item.icon }}</v-icon>
            <div class="nav-label">{{ item.title }}</div>
          </div>
        </router-link>

        <!-- Trader divider -->
        <div class="section-divider">
          <div class="section-label">Trader</div>
          <div class="section-line"></div>
        </div>

        <!-- Trader nav items -->
        <router-link
          v-for="item in traderNav"
          :key="item.name"
          :to="item.to"
          custom
          v-slot="{ navigate, isActive }"
        >
          <div
            class="nav-item"
            :class="{ active: isActive, disabled: item.disabled }"
            @click="!item.disabled && navigate($event)"
          >
            <v-icon size="16" :color="isActive ? '#F8F8F8' : '#333'">{{ item.icon }}</v-icon>
            <div class="nav-label">{{ item.title }}</div>
          </div>
        </router-link>
      </div>
    </div>

    <!-- Bottom section -->
    <div class="sidebar-bottom">
      <div class="bottom-divider"></div>
      <div class="bottom-items">
        <!-- Settings -->
        <router-link to="/settings" custom v-slot="{ navigate, isActive }">
          <div
            class="nav-item"
            :class="{ active: isActive }"
            @click="navigate"
          >
            <v-icon size="16" :color="isActive ? '#F8F8F8' : '#333'">fa-regular fa-cog</v-icon>
            <div class="nav-label">Settings</div>
          </div>
        </router-link>

        <!-- Quit to Communities -->
        <div class="nav-item" @click="$emit('quit-communities')">
          <v-icon size="16" color="#333">fa-regular fa-power-off</v-icon>
          <div class="nav-label">Quit to Communities</div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
export default {
  name: 'IBSidebar',

  computed: {
    communityName() {
      return this.$store.getters['ib/activeCommunity']?.name || 'Community'
    },

    communityAvatar() {
      const community = this.$store.getters['ib/activeCommunity']
      return community?.imageUrl || null
    },

    communityPrimaryColor() {
      const community = this.$store.getters['ib/activeCommunity']
      return '#' + (community?.primaryColor || 'C03131')
    },

    memberCount() {
      return this.$store.getters['ib/memberCount']
    },

    partnerName() {
      return this.$store.getters['ib/partnerName']
    },

    partnerLogo() {
      return this.$store.getters['ib/partnerLogoUrl']
    },

    sponsorLogo() {
      const sponsor = this.$store.getters['ib/communitySponsor']
      return sponsor?.brandLogo || sponsor?.logoUrl || ''
    },

    sponsorName() {
      const sponsor = this.$store.getters['ib/communitySponsor']
      return sponsor?.name || ''
    },

    communityNav() {
      return [
        { name: 'dashboard', title: 'Dashboard', icon: 'fa-regular fa-analytics', to: '/dashboard' },
        { name: 'events', title: 'Events', icon: 'fa-regular fa-calendar-alt', to: '/events' },
        { name: 'members', title: 'Members', icon: 'fa-regular fa-users', to: '/members' },
        { name: 'competitions', title: 'Competition', icon: 'fa-regular fa-trophy-alt', to: '/competitions' },
        { name: 'leaderboard', title: 'Leaderboard', icon: 'fa-regular fa-chart-bar', to: '/leaderboard' }
      ]
    },

    traderNav() {
      return [
        { name: 'journal', title: 'Journal', icon: 'fa-regular fa-book', to: '/journal' },
        { name: 'funding', title: 'Funding', icon: 'fa-regular fa-money-bill-wave', to: '/funding' },
        { name: 'plan', title: 'Plan', icon: 'fa-regular fa-credit-card', to: '/plan', disabled: true }
      ]
    }
  },

  methods: {
    getInitials(name) {
      if (!name) return '?'
      return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()
    }
  }
}
</script>

<style lang="scss" scoped>
.ib-sidebar {
  width: 259px;
  min-width: 259px;
  height: 100vh;
  position: sticky;
  top: 0;
  background: #F8F8F8;
  border-right: 2px solid #EBEBEB;
  border-radius: 0 5px 5px 5px;
  padding: 40px 15px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-y: auto;
  overflow-x: hidden;

  // Hide scrollbar but keep scroll
  scrollbar-width: none; // Firefox
  -ms-overflow-style: none; // IE/Edge
  &::-webkit-scrollbar {
    display: none; // Chrome/Safari
  }
}

// Top section
.sidebar-top {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

// Community header
.community-header {
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.community-identity {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
}

.community-avatar {
  width: 63px;
  height: 63px;
  min-width: 63px;
  border-radius: 50%;
  border: 2px solid #C03131;
  overflow: hidden;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.avatar-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Aeonik', sans-serif !important;
  font-weight: 700;
  font-size: 18px;
  color: #3B3B3B;
  background: #EBEBEB;
}

.community-name-wrap {
  flex: 1;
  min-width: 0;
}

.community-name {
  font-family: 'Aeonik', sans-serif !important;
  font-weight: 700 !important;
  font-size: 18px !important;
  line-height: 1.5;
  color: #14161B !important;
  word-wrap: break-word;
}

// Members chip
.members-chip {
  display: flex;
  gap: 7px;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  width: 100%;
  background: #FFBDBD;
  border-radius: 5px;
  height: 34px;
}

.members-label {
  flex: 1;
  font-family: 'Avenir', sans-serif;
  font-weight: 300;
  font-size: 14px;
  line-height: 1.5;
  color: #C03131;
}

.members-count {
  font-family: 'Avenir', sans-serif;
  font-weight: 800;
  font-size: 14px;
  line-height: 1.5;
  color: #C03131;
  text-align: right;
}

// Sponsored By
.sponsored-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  width: 100%;
}

.partner-logo-small {
  max-width: 160px;
  max-height: 36px;
  object-fit: contain;
}

.sponsored-text {
  font-family: 'Avenir', sans-serif;
  font-weight: 300;
  font-size: 14px;
  line-height: 1.5;
  color: #14161B;
}

.sponsor-name {
  font-weight: 700;
}

// Navigation
.nav-section {
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
}

// Section divider
.section-divider {
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
  margin-top: 15px;
  margin-bottom: 10px;

  &:first-child {
    margin-top: 0;
  }
}

.section-label {
  font-family: 'Avenir', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: 0.28px;
  color: #ABABAB;
  height: 27px;
  display: flex;
  align-items: center;
}

.section-line {
  width: 100%;
  height: 1px;
  border: 1px solid #EBEBEB;
}

// Nav items
.nav-item {
  display: flex;
  gap: 9px;
  align-items: center;
  padding: 5px 5px 5px 10px;
  border-radius: 10px;
  width: 229px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:not(.active):not(.disabled) {
    background: rgba(0, 0, 0, 0.04);
  }

  &.active {
    background: #C03131;

    .nav-label {
      color: #F8F8F8 !important;
    }
  }

  &.disabled {
    opacity: 0.4;
    cursor: default;
  }
}

.nav-label {
  font-family: 'Avenir', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: 0.28px;
  color: #3B3B3B;
}

// Bottom section
.sidebar-bottom {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.bottom-divider {
  width: 100%;
  height: 1px;
  border: 1px solid #EBEBEB;
}

.bottom-items {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>
